import { useCallback, useEffect, useState } from 'react'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { Search, CheckCircle, XCircle } from 'lucide-react'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { EsqueletoTarjetas } from '@/shared/components/Esqueleto'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { Notification, useNotification } from '@/shared/components/Notification'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import { usePermissions } from '@/shared/hooks/usePermissions'
import FilterSelect from '@/shared/components/FilterSelect'
import { RH } from '@/constants/permissions'
import { apiService } from '@/services/api'
import {
  vistaDeDireccion,
  type CandidatoParaDireccion,
  type EntrevistaDeDireccion,
} from '@/features/rh/api/vistaDeDireccion'
import {
  oGuion,
  formatoDeMonto,
  textoDelRango,
  textoDeCalificacion,
  sinEntrevistaDeRh,
  mensajeDeFallo,
} from '@/features/rh/lib/vistaDeDireccion'
import { EntrevistasDeDireccion } from '@/features/rh/components/EntrevistasDeDireccion'
import { AgendarEntrevistaDeDireccion } from '@/features/rh/components/AgendarEntrevistaDeDireccion'
import { MOTIVOS_DESCARTE } from '@/features/rh/lib/reclutamientoTablero'
import { AvisoDeEntrevistasPendientes } from '@/features/rh/components/AvisoDeEntrevistasPendientes'

type Decision = 'aprobar' | 'rechazar'

const Dato = ({ etiqueta, valor }: { etiqueta: string; valor: string }) => (
  <div>
    <p className="text-xs font-medium text-[#777777]">{etiqueta}</p>
    <p className="text-sm font-normal text-[#2F2F2F] mt-0.5 break-words">{valor}</p>
  </div>
)

/**
 * Vista de Dirección (SRH-283).
 *
 * Lista lo que RH mandó a validación y deja a Dirección decidir: aprobar,
 * rechazar, o agendar su propia entrevista cuyo resultado también decide.
 */
export const VistaDeDireccion = () => {
  const { hasPermission, isLoading: cargandoPermisos } = usePermissions()
  const { notification, showSuccess, showError, hideNotification } = useNotification()

  const puedeVer = hasPermission(RH.RECLUTAMIENTO_CONSULTAR_APROBADOS)
  const puedeDecidir = hasPermission(RH.RECLUTAMIENTO_DECIDIR_APROBADOS)

  const [candidatos, setCandidatos] = useState<CandidatoParaDireccion[]>([])
  const [cargando, setCargando] = useState(true)
  const [errorDeCarga, setErrorDeCarga] = useState<string | null>(null)
  const [busqueda, setBusqueda] = useState('')
  const [filtroVacante, setFiltroVacante] = useState('')

  const [decision, setDecision] = useState<
    { candidato: CandidatoParaDireccion; tipo: Decision } | null
  >(null)
  const [motivo, setMotivo] = useState('')
  const [comentario, setComentario] = useState('')
  const [trabajando, setTrabajando] = useState(false)

  const [agendandoPara, setAgendandoPara] = useState<CandidatoParaDireccion | null>(null)
  const [resultadoDe, setResultadoDe] = useState<EntrevistaDeDireccion | null>(null)
  const [recargaDeEntrevistas, setRecargaDeEntrevistas] = useState(0)

  const cargar = useCallback(async () => {
    setCargando(true)
    setErrorDeCarga(null)
    try {
      setCandidatos(await vistaDeDireccion.listar())
    } catch (error: unknown) {
      setErrorDeCarga(mensajeDeFallo(error))
      setCandidatos([])
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    if (cargandoPermisos || !puedeVer) return
    void cargar()
  }, [cargar, cargandoPermisos, puedeVer])

  const cerrarDecision = () => {
    setDecision(null)
    setMotivo('')
    setComentario('')
  }

  const ejecutarDecision = async () => {
    if (!decision) return
    const { candidato, tipo } = decision

    setTrabajando(true)
    try {
      if (tipo === 'aprobar') {
        await vistaDeDireccion.aprobar(candidato.candidateId)
      } else {
        await vistaDeDireccion.rechazar(candidato.candidateId, {
          reason: motivo,
          comment: comentario.trim() || undefined,
        })
      }
      // Decidido: sale de la vista sin recargar toda la pantalla.
      setCandidatos((previos) =>
        previos.filter((c) => c.candidateId !== candidato.candidateId),
      )
      cerrarDecision()
      showSuccess(
        'Decisión registrada',
        `${candidato.fullName} ${tipo === 'aprobar' ? 'aprobado' : 'rechazado'}: vuelve a RH, que confirma el cierre.`,
      )
    } catch (error: unknown) {
      const e = error as { statusCode?: number; status?: number }
      cerrarDecision()
      showError('No se pudo completar', mensajeDeFallo(error))
      if ((e?.statusCode ?? e?.status) === 409) void cargar()
    } finally {
      setTrabajando(false)
    }
  }

  const registrarResultado = async (datos: {
    result: 'approved' | 'rejected'
    reason?: string
    comments?: string
  }) => {
    if (!resultadoDe) return
    try {
      await vistaDeDireccion.registrarResultado(resultadoDe.id, datos)
      setResultadoDe(null)
      showSuccess(
        'Resultado registrado',
        datos.result === 'approved'
          ? 'Tu decisión quedó registrada: el candidato vuelve a RH, que confirma la selección.'
          : 'Tu decisión quedó registrada: el candidato vuelve a RH, que confirma el descarte.',
      )
      await cargar()
      setRecargaDeEntrevistas((n) => n + 1)
    } catch (error: unknown) {
      setResultadoDe(null)
      showError('No se pudo registrar', mensajeDeFallo(error))
      await cargar()
    }
  }

  if (!cargandoPermisos && !puedeVer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    )
  }

  const texto = busqueda.trim().toLowerCase()
  const visibles = candidatos.filter(
    (c) =>
      (texto === '' ||
        [c.fullName, c.profession, c.jobRequest?.position, c.jobRequest?.area]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(texto)) &&
      (filtroVacante === '' || String(c.jobRequest?.id ?? '') === filtroVacante),
  )

  const vacantes = Array.from(
    new Map(
      candidatos
        .filter((c) => c.jobRequest)
        .map((c) => [String(c.jobRequest!.id), c.jobRequest!.position]),
    ),
  ).map(([value, label]) => ({ value, label }))

  const faltaMotivo = decision?.tipo === 'rechazar' && !motivo

  return (
    <div className="mx-auto px-6 py-8 max-w-7xl">
      <div className="mb-6">
        <TituloDePantalla>Validación de Dirección</TituloDePantalla>
        <p className="text-[#1E1E1F] text-sm">
          Candidatos que RH envió a Dirección. Tu decisión se registra y RH confirma el cierre.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
        <div className="relative w-full max-w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar candidato..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-[#315FB9]"
          />
        </div>
        <FilterSelect
          label="Vacante"
          ancho="contenido"
          options={vacantes}
          value={filtroVacante}
          onChange={setFiltroVacante}
        />
        <p className="ml-auto flex items-center gap-2.5 text-sm text-gray-600 whitespace-nowrap">
          Candidatos
          <span className="inline-flex items-center justify-center rounded bg-[#EDFAFE] px-1.5 py-0.5 text-sm font-medium leading-6 text-[#315FB9] tabular-nums">
            {visibles.length.toLocaleString('es-MX')}
          </span>
        </p>
      </div>

      {errorDeCarga && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-sm text-red-700">{errorDeCarga}</p>
        </div>
      )}

      {cargando ? (
        <EsqueletoTarjetas cuantas={2} />
      ) : visibles.length === 0 ? (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
          <p className="text-gray-600">
            {busqueda || filtroVacante
              ? 'Ningún candidato coincide con los filtros'
              : 'No hay candidatos esperando la validación de Dirección'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {visibles.map((c) => (
            <article
              key={c.candidateId}
              className="bg-white rounded-xl border border-[#E0E0E0] shadow-sm"
            >
              <header className="flex flex-wrap items-start justify-between gap-3 px-6 py-4 border-b border-[#E0E0E0]">
                <div>
                  <h2 className="text-base font-semibold text-[#1E1E1F]">
                    {c.fullName}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {oGuion(c.jobRequest?.position)} · {oGuion(c.jobRequest?.area)}
                  </p>
                </div>
                {puedeDecidir && (
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setDecision({ candidato: c, tipo: 'aprobar' })}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#315FB9] hover:underline"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aprobar
                    </button>
                    <button
                      type="button"
                      onClick={() => setDecision({ candidato: c, tipo: 'rechazar' })}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#EF4444] hover:underline"
                    >
                      <XCircle className="w-4 h-4" />
                      Rechazar
                    </button>
                  </div>
                )}
              </header>

              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <Dato etiqueta="Profesión" valor={oGuion(c.profession)} />
                  <Dato
                    etiqueta="Experiencia"
                    valor={
                      c.experienceYears === null
                        ? '—'
                        : `${c.experienceYears} ${c.experienceYears === 1 ? 'año' : 'años'}`
                    }
                  />
                  <Dato etiqueta="Ciudad de residencia" valor={oGuion(c.residenceCity)} />
                  <Dato etiqueta="Disponibilidad" valor={oGuion(c.availability)} />
                  <Dato
                    etiqueta="Sueldo anterior"
                    valor={formatoDeMonto(c.previousMonthlySalary)}
                  />
                  <Dato
                    etiqueta="Expectativa salarial"
                    valor={formatoDeMonto(c.salaryExpectation)}
                  />
                  <Dato
                    etiqueta="Propuesta salarial de RH"
                    valor={textoDelRango(c.hrSalaryRange)}
                  />
                  <Dato
                    etiqueta="Calificación de RH"
                    valor={textoDeCalificacion(c.hrInterview)}
                  />
                </div>

                {sinEntrevistaDeRh(c.hrInterview) ? (
                  <p className="text-sm text-gray-500">
                    Este candidato entró por alta manual y no tuvo entrevista de RH.
                  </p>
                ) : (
                  c.hrInterview?.comments && (
                    <div>
                      <p className="text-xs font-medium text-[#777777]">
                        Comentarios de la entrevista de RH
                      </p>
                      <p className="text-sm text-[#2F2F2F] mt-0.5 break-words">
                        {c.hrInterview.comments}
                      </p>
                    </div>
                  )
                )}

                <EntrevistasDeDireccion
                  candidateId={c.candidateId}
                  puedeDecidir={puedeDecidir}
                  onAgendar={() => setAgendandoPara(c)}
                  onRegistrarResultado={setResultadoDe}
                  recargaEn={recargaDeEntrevistas}
                />
              </div>
            </article>
          ))}
        </div>
      )}

      {decision && (
        <CapaDeModal onClickFondo={trabajando ? undefined : cerrarDecision}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <h2 className="text-base font-semibold text-[#1E1E1F]">
              {decision.tipo === 'aprobar' ? 'Aprobar candidato' : 'Rechazar candidato'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {decision.tipo === 'aprobar'
                ? 'Tu decisión se registra y el candidato vuelve a RH, que confirma la selección. No se le avisa al candidato todavía.'
                : 'Tu decisión se registra y el candidato vuelve a RH, que confirma el descarte. El motivo es obligatorio.'}
            </p>
            <p className="text-sm text-gray-700 mt-3">
              Candidato:{' '}
              <span className="font-medium">{decision.candidato.fullName}</span>
            </p>

            {/* SRH-312: las entrevistas sin resultado se cancelan al decidir. */}
            <AvisoDeEntrevistasPendientes candidateId={decision.candidato.candidateId} />

            {decision.tipo === 'rechazar' && (
              <div className="mt-4">
                <label
                  htmlFor="motivo-direccion"
                  className="block text-xs font-medium text-[#777777] mb-1.5"
                >
                  Motivo <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  id="motivo-direccion"
                  value={motivo}
                  onChange={setMotivo}
                  placeholder="Selecciona el motivo"
                  placeholderDeBusqueda="Buscar motivo..."
                  opciones={MOTIVOS_DESCARTE.map((m) => ({ value: m, label: m }))}
                />
              </div>
            )}

            {/* El comentario solo va en el rechazo: aprobar no lleva cuerpo en
                el endpoint de Dirección, así que pedirlo al aprobar era
                recoger algo que no se guardaba en ningún lado. */}
            {decision.tipo === 'rechazar' && (
              <div className="mt-4">
                <label
                  htmlFor="comentario-direccion"
                  className="block text-xs font-medium text-[#777777] mb-1.5"
                >
                  Comentario{' '}
                  <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  id="comentario-direccion"
                  rows={3}
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-[#477FFA]"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={cerrarDecision}
                disabled={trabajando}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void ejecutarDecision()}
                disabled={trabajando || faltaMotivo}
                className={estiloDeBoton(
                  decision.tipo === 'rechazar' ? 'peligro' : 'exito',
                )}
              >
                {trabajando ? 'Guardando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {agendandoPara && (
        <AgendarEntrevistaDeDireccion
          candidato={agendandoPara}
          // getAllUsers y no getUsers: este ultimo descarta a los MASTER, y el
          // permiso de decidir por Direccion lo puede tener cualquiera.
          cargarUsuarios={() => apiService.getAllUsers()}
          onCancelar={() => setAgendandoPara(null)}
          onAgendada={() => {
            setAgendandoPara(null)
            setRecargaDeEntrevistas((n) => n + 1)
            showSuccess('Entrevista agendada', 'El candidato sigue en validación.')
          }}
        />
      )}

      {resultadoDe && (
        <ResultadoDeEntrevista
          entrevista={resultadoDe}
          onCancelar={() => setResultadoDe(null)}
          onGuardar={registrarResultado}
        />
      )}

      {notification?.isVisible && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          isVisible={notification.isVisible}
          onClose={hideNotification}
        />
      )}
    </div>
  )
}

/** El resultado de una entrevista: decide, así que pide confirmación aparte. */
const ResultadoDeEntrevista = ({
  entrevista,
  onCancelar,
  onGuardar,
}: {
  entrevista: EntrevistaDeDireccion
  onCancelar: () => void
  onGuardar: (datos: {
    result: 'approved' | 'rejected'
    reason?: string
    comments?: string
  }) => Promise<void>
}) => {
  const [resultado, setResultado] = useState<'approved' | 'rejected'>('approved')
  const [motivo, setMotivo] = useState('')
  const [comentarios, setComentarios] = useState('')
  const [guardando, setGuardando] = useState(false)

  const faltaMotivo = resultado === 'rejected' && !motivo

  return (
    <CapaDeModal onClickFondo={guardando ? undefined : onCancelar}>
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <h2 className="text-base font-semibold text-[#1E1E1F]">
          Registrar resultado
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          El resultado es tu decisión: se registra y el candidato vuelve a RH, que
          confirma la selección o el descarte. No se puede cambiar después.
        </p>

        {/* Las demás entrevistas sin resultado se cancelan al registrar este. */}
        <AvisoDeEntrevistasPendientes
          candidateId={entrevista.candidateId}
          exceptoId={entrevista.id}
        />

        <div className="mt-4">
          <label
            htmlFor="resultado-entrevista"
            className="block text-xs font-medium text-[#777777] mb-1.5"
          >
            Resultado <span className="text-red-500">*</span>
          </label>
          <SelectBuscable
            variante="formulario"
            id="resultado-entrevista"
            value={resultado}
            onChange={(valor) => setResultado(valor as 'approved' | 'rejected')}
            canDelete={false}
            placeholderDeBusqueda="Buscar resultado..."
            opciones={[
              { value: 'approved', label: 'Aprobada — RH confirma la selección' },
              { value: 'rejected', label: 'Rechazada — RH confirma el descarte' },
            ]}
          />
        </div>

        {resultado === 'rejected' && (
          <div className="mt-4">
            <label
              htmlFor="motivo-resultado"
              className="block text-xs font-medium text-[#777777] mb-1.5"
            >
              Motivo <span className="text-red-500">*</span>
            </label>
            <SelectBuscable
                variante="formulario"
                id="motivo-resultado"
                value={motivo}
                onChange={setMotivo}
                placeholder="Selecciona el motivo"
                placeholderDeBusqueda="Buscar motivo..."
                opciones={MOTIVOS_DESCARTE.map((m) => ({ value: m, label: m }))}
              />
          </div>
        )}

        <div className="mt-4">
          <label
            htmlFor="comentarios-resultado"
            className="block text-xs font-medium text-[#777777] mb-1.5"
          >
            Comentarios{' '}
            <span className="text-gray-400 font-normal">(opcional)</span>
          </label>
          <textarea
            id="comentarios-resultado"
            rows={3}
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-[#477FFA]"
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCancelar}
            disabled={guardando}
            className={estiloDeBoton('secundario')}
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={guardando || faltaMotivo}
            onClick={() => {
              setGuardando(true)
              void onGuardar({
                result: resultado,
                reason: resultado === 'rejected' ? motivo : undefined,
                comments: comentarios.trim() || undefined,
              }).finally(() => setGuardando(false))
            }}
            className={estiloDeBoton('primario')}
          >
            {guardando ? 'Guardando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </CapaDeModal>
  )
}

export default VistaDeDireccion
